import { $ } from '../common/dom.ts';
import { Modal } from '../common/modal.ts';
import type { SoundBoard } from './sound.ts';
import { SoundSettings } from './sound-panel.ts';
import type { WorldRenderer } from './world-renderer.ts';

const ZOOM_KEY = 'pocket_zoom';
const ECONOMY_KEY = 'pocket_economy';
/** Crans de zoom, du plus loin au plus près (0 : la taille choisie d'après l'écran). */
const ZOOM_LEVELS = [
  { value: -2, label: 'Très loin' },
  { value: -1, label: 'Loin' },
  { value: 0, label: 'Normal' },
  { value: 1, label: 'Proche' },
  { value: 2, label: 'Très proche' },
] as const;

function savedZoom(): number {
  try {
    const zoom = Number(localStorage.getItem(ZOOM_KEY) ?? 0);
    return ZOOM_LEVELS.some((level) => level.value === zoom) ? zoom : 0;
  } catch {
    return 0;
  }
}

function savedEconomy(): boolean {
  try {
    return localStorage.getItem(ECONOMY_KEY) === '1';
  } catch {
    return false;
  }
}

/** Options du jeu : son (voir sound-panel.ts), zoom sur la carte et mode économie, retenus dans ce navigateur. */
export class OptionsPanel extends Modal {
  private readonly zoom = $<HTMLInputElement>('#option-zoom', this.root);
  private readonly economy = $<HTMLInputElement>('#option-economy', this.root);
  private readonly renderer: WorldRenderer;
  private readonly sound: SoundSettings;
  private currentZoom = savedZoom();

  constructor(renderer: WorldRenderer, sounds: SoundBoard) {
    super('#options');
    this.renderer = renderer;
    this.sound = new SoundSettings(this.root, sounds);
    renderer.setZoom(this.currentZoom);
    renderer.setEconomy(savedEconomy());
    this.economy.addEventListener('change', () => {
      renderer.setEconomy(this.economy.checked);
      try {
        localStorage.setItem(ECONOMY_KEY, this.economy.checked ? '1' : '0');
      } catch {
        // Pas de stockage : le mode vaut pour cette visite seulement.
      }
    });
    $('#btn-options').addEventListener('click', () => this.toggle());
    this.zoom.min = '0';
    this.zoom.max = String(ZOOM_LEVELS.length - 1);
    this.zoom.addEventListener('input', () => {
      const level = ZOOM_LEVELS[Number(this.zoom.value)] ?? ZOOM_LEVELS[2];
      this.currentZoom = level.value;
      this.renderer.setZoom(level.value);
      try {
        localStorage.setItem(ZOOM_KEY, String(level.value));
      } catch {
        // Pas de stockage : le zoom vaut pour cette visite seulement.
      }
      this.render();
    });
  }

  protected override reveal(): void {
    return this.show();
  }

  show(): void {
    this.open();
    this.render();
  }

  private render(): void {
    this.economy.checked = savedEconomy();
    const index = Math.max(0, ZOOM_LEVELS.findIndex((level) => level.value === this.currentZoom));
    this.zoom.value = String(index);
    $('#option-zoom-value', this.root).textContent = ZOOM_LEVELS[index]?.label ?? 'Normal';
    this.sound.render();
  }
}
